import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Calculator, FileDown, Save, Ruler, DollarSign, Clock, History, RotateCcw, Package, Wallet } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function LaborEstimation() {
  const [sqft, setSqft] = useState("");
  const [cost, setCost] = useState("");
  const [rate, setRate] = useState("");
  const [hours, setHours] = useState("");
  const [material, setMaterial] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const reportRef = useRef(null);

  const loadHistory = async () => {
    try { const { data } = await api.get("/labor/history"); setHistory(data); } catch { /* noop */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const calculate = () => {
    setError("");
    const sf = Number(sqft);
    const c  = Number(cost);
    const rRaw = String(rate).trim();
    const hRaw = String(hours).trim();
    const r  = rRaw === "" ? NaN : Number(rRaw);
    const hIn = hRaw === "" ? NaN : Number(hRaw);
    // Material is OPTIONAL — estimator can leave it blank to only run labor.
    const mRaw = String(material).trim();
    const m  = mRaw === "" ? 0 : Number(mRaw);
    if (!Number.isFinite(sf) || sf <= 0 ||
        !Number.isFinite(c)  || c  <= 0 ||
        (mRaw !== "" && (!Number.isFinite(m) || m < 0))) {
      setError("Please enter valid SQFT and Cost");
      setResult(null);
      return;
    }
    // Either Labor Rate OR Labor Hours is required. Labor Rate takes
    // precedence when both are supplied so a stale hours field doesn't
    // override a fresh rate.
    let laborRate; let laborHours;
    if (Number.isFinite(r) && r > 0) {
      laborRate  = r;
      laborHours = c / r;
    } else if (Number.isFinite(hIn) && hIn > 0) {
      laborHours = hIn;
      laborRate  = c / hIn;
    } else {
      setError("Enter either Labor Rate OR Labor Hours (need at least one to compute productivity)");
      setResult(null);
      return;
    }
    const cost_per_sqft = c / sf;
    const sf_per_hour = sf / laborHours;
    const material_cost_per_sqft = sf > 0 ? m / sf : 0;
    const total_cost = c + m;
    const total_cost_per_sqft = sf > 0 ? total_cost / sf : 0;
    setResult({
      sqft: sf, cost: c, rate: laborRate, material: m,
      labor_hours: laborHours, cost_per_sqft, sf_per_hour,
      material_cost_per_sqft, total_cost, total_cost_per_sqft,
      rate_was_derived: !(Number.isFinite(r) && r > 0),
    });
  };

  const clearAll = () => {
    setSqft(""); setCost(""); setRate(""); setHours(""); setMaterial(""); setResult(null); setError("");
  };

  const saveCalc = async () => {
    if (!result) return;
    try {
      await api.post("/labor/save", {
        estimated_sqft: result.sqft,
        estimated_cost: result.cost,
        labor_rate: result.rate,
        labor_hours: result.labor_hours,
        cost_per_sqft: result.cost_per_sqft,
        sf_per_hour: result.sf_per_hour,
        estimated_material_cost: result.material,
        material_cost_per_sqft: result.material_cost_per_sqft,
        total_cost: result.total_cost,
        total_cost_per_sqft: result.total_cost_per_sqft,
      });
      toast.success("Calculation saved");
      loadHistory();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportPdf = async () => {
    if (!reportRef.current || !result) return;
    toast.info("Rendering PDF…");
    const canvas = await html2canvas(reportRef.current, {
      backgroundColor: "#0A0B10", scale: 2, useCORS: true,
    });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const ratio = pageW / canvas.width;
    pdf.addImage(img, "PNG", 0, 20, pageW, canvas.height * ratio);
    pdf.save(`Labor_Estimate_${Date.now()}.pdf`);
    toast.success("PDF downloaded");
  };

  const inputCls = "w-full bg-[#0A0B10] border border-border px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#00D4FF] transition-colors";

  return (
    <div className="space-y-6" data-testid="labor-estimation-page">
      <header>
        <div className="text-[11px] tracking-[0.35em] uppercase text-[#00D4FF]">ACCOUNTING · TOOL</div>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-2">Labor Estimation Tool</h1>
        <p className="text-sm text-muted-foreground mt-1">Quick labor-hour and productivity estimator for accounting use.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-1 bg-[#12141D] border border-border p-6">
          <div className="flex items-center gap-2 mb-5">
            <Calculator className="text-[#00D4FF]" size={18} />
            <h2 className="font-heading font-semibold">Inputs</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2"><Ruler size={11}/> Estimated SQFT</label>
              <input data-testid="labor-sqft" type="number" min="0" step="any" value={sqft} onChange={(e) => setSqft(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2"><DollarSign size={11}/> Estimated Cost</label>
              <input data-testid="labor-cost" type="number" min="0" step="any" value={cost} onChange={(e) => setCost(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2"><Clock size={11}/> Labor Rate ($/hour) <span className="text-[9px] text-muted-foreground/70 normal-case tracking-normal">(or Hours below)</span></label>
              <input data-testid="labor-rate" type="number" min="0" step="any" value={rate} onChange={(e) => setRate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2"><Clock size={11}/> Labor Hours <span className="text-[9px] text-muted-foreground/70 normal-case tracking-normal">(use if no rate)</span></label>
              <input data-testid="labor-hours" type="number" min="0" step="any" value={hours} onChange={(e) => setHours(e.target.value)} className={inputCls} />
              <p className="text-[10px] text-muted-foreground mt-1.5">
                When Labor Rate is empty we derive it from Estimated Cost ÷ Labor Hours so productivity still computes.
              </p>
            </div>
            <div className="pt-2 border-t border-border/60">
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2">
                <Package size={11}/> Estimated Material Cost <span className="text-[9px] text-muted-foreground/70 normal-case tracking-normal">(optional)</span>
              </label>
              <input data-testid="labor-material" type="number" min="0" step="any" value={material} onChange={(e) => setMaterial(e.target.value)} className={inputCls} />
              <p className="text-[10px] text-muted-foreground mt-1.5">Adds material $/SF and combined labor+material totals to the estimate.</p>
            </div>

            {error && (
              <div data-testid="labor-error" className="text-xs text-red-300 border border-red-500/40 bg-red-500/5 px-3 py-2">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button data-testid="labor-calculate" type="button" onClick={calculate}
                className="flex-1 bg-[#00D4FF] text-[#0A0B10] font-semibold text-sm tracking-widest uppercase py-3 hover:bg-[#00B8D4] transition-colors inline-flex items-center justify-center gap-2">
                <Calculator size={14} /> Calculate
              </button>
              <button data-testid="labor-clear" type="button" onClick={clearAll}
                className="px-4 bg-transparent border border-border hover:border-[#00D4FF] text-xs uppercase tracking-widest inline-flex items-center gap-2 transition-colors">
                <RotateCcw size={12} /> Clear
              </button>
            </div>
          </div>
        </section>

        <section className="lg:col-span-2" ref={reportRef}>
          {!result && (
            <div className="bg-[#12141D] border border-dashed border-border p-10 text-center text-muted-foreground text-xs tracking-widest uppercase h-full flex items-center justify-center">
              Enter values and click Calculate
            </div>
          )}
          {result && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-[#12141D] border border-[#00D4FF]/40 p-5" data-testid="labor-card-hours">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Labor Hours Needed</div>
                  <div className="font-heading font-bold text-4xl tabular-nums mt-3">{result.labor_hours.toFixed(2)} <span className="text-sm text-muted-foreground">hrs</span></div>
                  <div className="h-[1px] w-8 bg-[#00D4FF] mt-3" />
                </div>
                <div className="bg-[#12141D] border border-border p-5" data-testid="labor-card-cost-sqft">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Labor Cost per SQFT</div>
                  <div className="font-heading font-bold text-4xl tabular-nums mt-3">${result.cost_per_sqft.toFixed(2)}</div>
                  <div className="h-[1px] w-8 bg-[#00D4FF] mt-3" />
                </div>
                <div className="bg-[#12141D] border border-border p-5" data-testid="labor-card-productivity">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Productivity (SQFT/Hour)</div>
                  <div className="font-heading font-bold text-4xl tabular-nums mt-3">{result.sf_per_hour.toFixed(2)}</div>
                  <div className="h-[1px] w-8 bg-[#00D4FF] mt-3" />
                </div>
              </div>

              {result.material > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="labor-material-section">
                  <div className="bg-[#12141D] border border-[#A78BFA]/40 p-5" data-testid="labor-card-material-total">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-1.5"><Package size={11}/> Material Cost</div>
                    <div className="font-heading font-bold text-4xl tabular-nums mt-3">{fmtMoney(result.material)}</div>
                    <div className="h-[1px] w-8 bg-[#A78BFA] mt-3" />
                  </div>
                  <div className="bg-[#12141D] border border-border p-5" data-testid="labor-card-material-per-sqft">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Material Cost per SQFT</div>
                    <div className="font-heading font-bold text-4xl tabular-nums mt-3">${result.material_cost_per_sqft.toFixed(2)}</div>
                    <div className="h-[1px] w-8 bg-[#A78BFA] mt-3" />
                  </div>
                  <div className="bg-[#12141D] border border-emerald-500/40 p-5" data-testid="labor-card-total-cost">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-1.5"><Wallet size={11}/> Total (Labor + Material)</div>
                    <div className="font-heading font-bold text-4xl tabular-nums mt-3 text-emerald-300">{fmtMoney(result.total_cost)}</div>
                    <div className="text-[11px] text-muted-foreground mt-1 font-mono">${result.total_cost_per_sqft.toFixed(2)}/SF</div>
                    <div className="h-[1px] w-8 bg-emerald-400 mt-3" />
                  </div>
                </div>
              )}

              <div className="bg-[#12141D] border border-border p-5">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Inputs Recap</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-sm">
                  <div><span className="text-muted-foreground">SQFT:</span> {result.sqft.toLocaleString()}</div>
                  <div><span className="text-muted-foreground">Labor Cost:</span> {fmtMoney(result.cost)}</div>
                  <div><span className="text-muted-foreground">Rate:</span> {fmtMoney(result.rate)}/hr{result.rate_was_derived && <span className="ml-1 text-[9px] uppercase tracking-widest text-[#00D4FF]">derived</span>}</div>
                  <div><span className="text-muted-foreground">Material:</span> {result.material > 0 ? fmtMoney(result.material) : "—"}</div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {result && (
        <section className="flex flex-wrap items-center gap-3 pt-2">
          <button data-testid="labor-export-pdf" onClick={exportPdf}
            className="bg-[#00D4FF] text-[#0A0B10] font-semibold px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#00B8D4] transition-colors">
            <FileDown size={14} /> Export to PDF
          </button>
          <button data-testid="labor-save" onClick={saveCalc}
            className="bg-transparent border border-border hover:border-[#00D4FF] px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 transition-colors">
            <Save size={14} /> Save Calculation
          </button>
        </section>
      )}

      {history.length > 0 && (
        <section className="bg-[#12141D] border border-border" data-testid="labor-history">
          <div className="p-5 pb-3 flex items-center gap-2">
            <History className="text-[#00D4FF]" size={16} />
            <h3 className="font-heading font-semibold">Calculation History</h3>
            <span className="text-xs text-muted-foreground ml-2">{history.length}</span>
          </div>
          <table className="w-full text-left">
            <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28]">
              <tr>
                <th className="py-3 px-5">Date & Time</th>
                <th className="py-3 px-5 text-right">SQFT</th>
                <th className="py-3 px-5 text-right">Labor Cost</th>
                <th className="py-3 px-5 text-right">Rate</th>
                <th className="py-3 px-5 text-right">Labor Hours</th>
                <th className="py-3 px-5 text-right">Labor $/SF</th>
                <th className="py-3 px-5 text-right">Material $</th>
                <th className="py-3 px-5 text-right">Material $/SF</th>
                <th className="py-3 px-5 text-right">Total $</th>
                <th className="py-3 px-5 text-right">Total $/SF</th>
                <th className="py-3 px-5 text-right">SQFT/Hr</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-border">
                  <td className="py-3 px-5 text-xs font-mono text-muted-foreground">{new Date(h.saved_at).toLocaleString()}</td>
                  <td className="py-3 px-5 text-sm font-mono text-right">{h.estimated_sqft.toLocaleString()}</td>
                  <td className="py-3 px-5 text-sm font-mono text-right">{fmtMoney(h.estimated_cost)}</td>
                  <td className="py-3 px-5 text-sm font-mono text-right">{fmtMoney(h.labor_rate)}</td>
                  <td className="py-3 px-5 text-sm font-mono text-right text-[#00D4FF]">{h.labor_hours.toFixed(2)}</td>
                  <td className="py-3 px-5 text-sm font-mono text-right">{fmtMoney(h.cost_per_sqft)}</td>
                  <td className="py-3 px-5 text-sm font-mono text-right">
                    {(h.estimated_material_cost || 0) > 0 ? fmtMoney(h.estimated_material_cost) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-3 px-5 text-sm font-mono text-right">
                    {(h.material_cost_per_sqft || 0) > 0 ? fmtMoney(h.material_cost_per_sqft) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-3 px-5 text-sm font-mono text-right text-emerald-300">
                    {fmtMoney(h.total_cost || h.estimated_cost)}
                  </td>
                  <td className="py-3 px-5 text-sm font-mono text-right">
                    {fmtMoney(h.total_cost_per_sqft || h.cost_per_sqft)}
                  </td>
                  <td className="py-3 px-5 text-sm font-mono text-right">{h.sf_per_hour.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
